import { fromBool } from '@execonline-inc/maybe-adapter';
import { assertNever, noop } from '@kofno/piper';
import { Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import ParticipantsStore from '../../../../../ParticipantsStore';
import {
  fieldsToTemplate,
  fieldsToValues,
  findField,
  FormTemplate,
} from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import { LicenseResource } from '../../Table/LicenseAllocations/Types';
import {
  BulkParticipantsLicenseFormResource,
  convertLicensesToSelectionOptions,
  createUnselectedLicense,
  fetchingLicenseOptions,
  fetchingLicenseOptionsError,
  FetchingLicenseOptionsErrorType,
  filterSelectedLicenses,
  initialReady,
  LicensesReady,
  licensesReady,
  LoadError,
  loading,
  loadingError,
  missingTemplate,
  NewBulkLicenseAllocationsResource,
  ParticipantBulkLicenseSubmittedResource,
  SelectableLicense,
  State,
  SubmitError,
  submitted,
  Submitting,
  submitting,
  submittingError,
  waiting,
} from './Types';

export class AddLicenseToParticipantsStore {
  @observable
  state: State = waiting();

  constructor() {
    makeObservable(this);
  }

  @action
  waiting = (): void => {
    this.state = waiting();
  };

  @action
  loading = (link: Link, participantsStore: ParticipantsStore): void => {
    switch (this.state.kind) {
      case 'waiting':
        this.state = loading(link, participantsStore);
        break;
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'licenses-ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingError = (error: LoadError): void => {
    switch (this.state.kind) {
      case 'loading':
        this.state = loadingError(error);
        break;
      case 'waiting':
      case 'loading-error':
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'licenses-ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  initialReady = (resource: NewBulkLicenseAllocationsResource): void => {
    switch (this.state.kind) {
      case 'loading':
        const template = resource.templates.self;
        this.state = template
          ? initialReady(resource, template, this.state.participantsStore)
          : loadingError(missingTemplate('self'));
        break;
      case 'waiting':
      case 'loading-error':
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'licenses-ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  licensesReady = (resource: BulkParticipantsLicenseFormResource): void => {
    switch (this.state.kind) {
      case 'fetching-license-options':
      case 'fetching-license-options-error':
        const template = resource.templates.self;
        this.state = template
          ? licensesReady(
              resource,
              template,
              this.state.participantsStore,
              [],
              this.state.resource,
              this.state.template,
            )
          : loadingError(missingTemplate('self'));
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'licenses-ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fetchingLicenseOptions = (): void => {
    switch (this.state.kind) {
      case 'initial-ready':
        // Just transition to fetching state - Reactions will handle the API call
        this.state = fetchingLicenseOptions(
          this.state.resource,
          this.state.template,
          this.state.participantsStore,
        );
        break;
      case 'licenses-ready':
        // Copy the current license_action field value to the initial template
        const currentState = this.state;
        const licenseActionField = findField(
          'license_action',
          'single-selection',
          currentState.template.fields,
        );

        // Create updated template with current license action value
        const updatedTemplate = licenseActionField
          .map(
            (currentField): FormTemplate => ({
              ...currentState.initialTemplate,
              fields: currentState.initialTemplate.fields.map((field) =>
                field.kind === 'single-selection' && field.name === 'license_action'
                  ? { ...field, value: currentField.value }
                  : field,
              ),
            }),
          )
          .getOrElse(() => currentState.initialTemplate);

        // Use initial resource when re-fetching from licenses-ready state
        this.state = fetchingLicenseOptions(
          currentState.initialResource,
          updatedTemplate,
          currentState.participantsStore,
        );
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  fetchingLicenseOptionsError = (error: FetchingLicenseOptionsErrorType): void => {
    switch (this.state.kind) {
      case 'fetching-license-options':
        this.state = fetchingLicenseOptionsError(
          this.state.resource,
          this.state.template,
          error,
          this.state.participantsStore,
        );
        break;
      case 'fetching-license-options-error':
        this.state = fetchingLicenseOptionsError(
          this.state.resource,
          this.state.template,
          error,
          this.state.participantsStore,
        );
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'licenses-ready':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitting = (): void => {
    switch (this.state.kind) {
      case 'licenses-ready':
        // Sync licenses to template before extracting values
        this.syncLicensesToTemplate();

        const {
          resource,
          template,
          participantsStore,
          initialResource,
          initialTemplate,
          selectedLicenses,
        } = this.state;
        this.state = fieldsToValues(template.fields)
          .mapError(fieldsToTemplate(template))
          .cata<Submitting | LicensesReady>({
            Ok: (values) => submitting(resource, template, values, participantsStore),
            Err: (errTemplate) =>
              licensesReady(
                resource,
                errTemplate,
                participantsStore,
                selectedLicenses,
                initialResource,
                initialTemplate,
              ),
          });
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submittingError = (error: SubmitError): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submittingError(this.state.resource, this.state.template, error);
        break;
      case 'fetching-license-options':
      case 'fetching-license-options-error':
        // Can't show submitting error from fetching-license-options since resource type is wrong
        // This should probably be a loadingError instead
        this.state = loadingError(error);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'licenses-ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitted = (resource: ParticipantBulkLicenseSubmittedResource): void => {
    switch (this.state.kind) {
      case 'submitting':
        this.state = submitted(resource, this.state.template);
        break;
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'licenses-ready':
      case 'submitting-error':
      case 'submitted':
        break;
      default:
        assertNever(this.state);
    }
  };

  // License Selection Actions
  @action
  initializeLicenses = (): void => {
    if (this.state.kind !== 'licenses-ready') return;
    if (this.state.selectedLicenses.length === 0) {
      this.state = licensesReady(
        this.state.resource,
        this.state.template,
        this.state.participantsStore,
        [createUnselectedLicense()],
        this.state.initialResource,
        this.state.initialTemplate,
      );
    }
  };

  @action
  addLicense = (): void => {
    if (this.state.kind !== 'licenses-ready') return;
    this.state = licensesReady(
      this.state.resource,
      this.state.template,
      this.state.participantsStore,
      [...this.state.selectedLicenses, createUnselectedLicense()],
      this.state.initialResource,
      this.state.initialTemplate,
    );
  };

  @action
  removeLicenseAtIndex = (index: number): void => {
    if (this.state.kind !== 'licenses-ready') return;
    this.state = licensesReady(
      this.state.resource,
      this.state.template,
      this.state.participantsStore,
      this.state.selectedLicenses.filter((_, i) => i !== index),
      this.state.initialResource,
      this.state.initialTemplate,
    );
  };

  @action
  updateLicenseAtIndex = (index: number, license: SelectableLicense): void => {
    if (this.state.kind !== 'licenses-ready') return;
    const newLicenses = [...this.state.selectedLicenses];
    newLicenses[index] = license;
    this.state = licensesReady(
      this.state.resource,
      this.state.template,
      this.state.participantsStore,
      newLicenses,
      this.state.initialResource,
      this.state.initialTemplate,
    );
  };

  @action
  syncLicensesToTemplate = (): void => {
    if (this.state.kind !== 'licenses-ready') return;

    const { selectedLicenses, resource } = this.state;
    const field = findField(
      'product_license_ids',
      'multiple-selection',
      this.state.template.fields,
    );
    field.cata({
      Just: (f) => {
        const selectedOptions = convertLicensesToSelectionOptions(
          selectedLicenses,
          resource.payload.allocatableOrReleasableLicenses,
        );
        f.value = selectedOptions;
      },
      Nothing: noop,
    });
  };

  // Memoized computation of selected license IDs for performance
  @computed
  get selectedLicenseIds(): ReadonlyArray<number> {
    if (this.state.kind !== 'licenses-ready') return [];
    return filterSelectedLicenses(this.state.selectedLicenses).map((l) => l.licenseId);
  }

  getAvailableLicenses = (currentLicense: SelectableLicense): ReadonlyArray<LicenseResource> => {
    if (this.state.kind !== 'licenses-ready') return [];

    const licenses = this.state.resource.payload.allocatableOrReleasableLicenses;

    return licenses.filter(
      (license) =>
        !this.selectedLicenseIds.includes(license.payload.id) ||
        (currentLicense.kind === 'selected' && currentLicense.licenseId === license.payload.id),
    );
  };

  hasAvailableLicensesToAdd = (): boolean => {
    if (this.state.kind !== 'licenses-ready') return false;
    return this.getAvailableLicenses(createUnselectedLicense()).length > 0;
  };

  @computed
  get allParticipantsFromSameOrg(): Maybe<boolean> {
    // Check if state has participantsStore
    switch (this.state.kind) {
      case 'initial-ready':
      case 'fetching-license-options':
      case 'fetching-license-options-error':
      case 'licenses-ready':
        const participants = this.state.participantsStore.checkedParticipants;
        const organizations = participants.map((p) => p.resource.payload.organization);

        // Compact to remove empty/null/undefined
        const compactedOrgs = organizations.filter((org) => org && org.trim() !== '');

        // Check if all organizations are the same using Set
        const allSame = compactedOrgs.length > 0 && new Set(compactedOrgs).size === 1;

        return fromBool(allSame, true);
      case 'waiting':
      case 'loading':
      case 'loading-error':
      case 'submitting':
      case 'submitting-error':
      case 'submitted':
        return nothing();
      default:
        assertNever(this.state);
    }
  }

  @computed
  get canSubmit(): boolean {
    return (
      this.state.kind === 'licenses-ready' &&
      this.selectedLicenseIds.length > 0 &&
      this.allParticipantsFromSameOrg.getOrElse(() => false)
    );
  }
}
